import { useEffect, useState } from 'react'
import { TBButton } from '../../../Components/Commons/Buttons/TBButton.react'
import TBInput from '../../../Components/Commons/Input.react'
import TBDateRangePicker, {
  StartEndDatesStrings,
} from '../../../Components/Commons/Dates/TBDateRangePicker.react'
import { Stack } from '../../../Components/Stack'
import { Label, Select, Error } from './Elements'
import useFetch from '../../../Utilities/API/useFetch.react'
import { getDefaultDates } from '../../../Utilities/utilities'
import { sourceCleaner } from '../../../Utilities/Helpers/accountTypes'

export const reportTypes = {
  DemoReport: 'Demo Report',
  SqlARF: 'Asset Roll Forward',
  ExpenseReport: 'Expense Report',
  RevenueReport: 'Revenue Report',
  JournalEntryReport: 'Journal Entry Report',
  JournalEntrySummaryReport: 'Journal Entry Summary Report',
  GainLossReport: 'Gain/Loss Report',
  GainLossSummaryReport: 'Gain/Loss Summary Report',
  TransactionExport: 'Control - Export All Transactions',
  TransactionSummaryReport: 'Control - Transaction Count Summary Report',
  InventorySummary: 'Inventory Summary Report',
} as const
export type ReportTypeKey = keyof typeof reportTypes
const reportTypeKeys = Object.keys(reportTypes) as ReportTypeKey[]

const filtersBySource = {
  TransactionExport: true,
  JournalEntryReport: true,
  SqlARF: true,
} as Record<ReportTypeKey, boolean | undefined>

export type GenerateReportHandlerProps = {
  reportName: ReportTypeKey
  reportLabel: string
  source?: string
  startDate: string
  endDate: string
}

type GenerateReportProps = {
  generateReportHandler: (payload: GenerateReportHandlerProps) => void
  error?: boolean
}

const GenerateReport = ({
  generateReportHandler,
  error,
}: GenerateReportProps) => {
  const [fetchUrl] = useFetch()
  const [reportType, setReportType] = useState<ReportTypeKey>(reportTypeKeys[0])
  const [reportLabel, setReportLabel] = useState('')
  const [dates, setDates] = useState(() => getDefaultDates())
  const [source, setSource] = useState<string | undefined>(undefined)
  const [exchangeSources, setExchangeSources] = useState<string[]>([])
  const sourceAllowed = filtersBySource[reportType]

  useEffect(() => {
    const sources = fetchUrl('fetchExchangeSources') as string[]
    setExchangeSources(sources.sort())
  }, [fetchUrl])

  const handleDateChange = (dates: StartEndDatesStrings) =>
    // Remove milliseconds from ISO date string
    setDates({
      startDate: dates.startDate.replace(/\.[0-9]{3}/, ''),
      endDate: dates.endDate.replace(/\.[0-9]{3}/, ''),
    })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        generateReportHandler({
          reportLabel: reportLabel || 'Untitled Report',
          reportName: reportType,
          source: sourceAllowed ? sourceCleaner(source) : undefined,
          ...dates,
        })
      }}
    >
      <Stack gapSize="24px">
        <TBInput
          label="Label"
          value={reportLabel}
          placeholder="e.g. Revenue Report"
          handleChange={(e) => setReportLabel(e.target.value)}
        />
        <Label>
          Type
          <Select
            value={reportType}
            onChange={(e) => setReportType(e.target.value as ReportTypeKey)}
          >
            {(Object.keys(reportTypes) as ReportTypeKey[]).map((key) => (
              <option key={key} value={key}>
                {reportTypes[key]}
              </option>
            ))}
          </Select>
        </Label>
        <Label>
          Date Range
          <TBDateRangePicker
            isCustomDate
            filter={dates}
            setDateFilter={handleDateChange}
            readOnly={true}
            utc
          />
        </Label>
        {sourceAllowed && (
          <Label>
            Source (optional)
            <Select
              value={source}
              onChange={({ target: { value } }) =>
                setSource(value === 'Any' ? undefined : value)
              }
            >
              {exchangeSources.map((source) => (
                <option key={`SOURCE-${source}`} value={source}>
                  {source}
                </option>
              ))}
            </Select>
          </Label>
        )}
        {error && <Error>There was an error generating the report</Error>}
        <TBButton type="submit">Generate</TBButton>
      </Stack>
    </form>
  )
}

export default GenerateReport
